import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { LuX } from "react-icons/lu";

import brk from "../../util/breakpoints";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Wrapper = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: rgba(8, 10, 16, 0.78);
  backdrop-filter: blur(3px);
  z-index: 500;
  animation: ${fadeIn} 0.25s ease-out;
`;

const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  gap: 26px;
  width: 100%;
  max-width: ${(props) => `${props.$maxWidth}px`};
  max-height: 100%;
  padding: 36px;
  border: 1px solid var(--borderStrong);
  border-radius: 22px;
  background: var(--surface);
  box-shadow: 0px 28px 80px 0px rgba(0, 0, 0, 0.44);
  overflow-y: auto;
  outline: none;
  animation: ${popIn} 0.3s ease-out;
  @media ${brk.device.tablet} {
    gap: 20px;
    padding: 22px;
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border: 1px solid var(--border);
  border-radius: 50%;
  outline: 1px solid transparent;
  outline-offset: -1px;
  background: var(--card);
  color: var(--text);
  cursor: pointer;
  transition: outline-color 0.2s, transform 0.2s;
  &:hover {
    outline-color: var(--textDim);
    transform: rotate(90deg);
  }
`;

export function Dismiss(props) {
  return (
    <DismissButton onClick={props.onClick} aria-label="Close">
      <LuX size={16} />
    </DismissButton>
  );
}

// Shared dialog shell: dimmed backdrop, Escape / backdrop click to close, and page scroll lock.
function Modal(props) {
  const { onClose } = props;
  const dialogRef = useRef(null);

  // Close on Escape, stop the page scrolling behind the dialog, and move focus into it
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    if (dialogRef.current) dialogRef.current.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return (
    <Wrapper
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={props.labelledBy}
        tabIndex={-1}
        $maxWidth={props.maxWidth || 860}
      >
        {props.children}
      </Dialog>
    </Wrapper>
  );
}

export default Modal;
